// Axios instance for the admin app (PLA-86).
//
// baseURL = VITE_API_BASE_URL + "/api"; the request interceptor attaches the
// admin access token; the response interceptor refreshes via
// /admin/auth/refresh on a 401 then retries the original request, and logs out
// (clears storage + redirects to /login) when the refresh fails.
//
// Mirrors apps/frontend/src/api/client.ts, scoped to the admin token + the
// /admin/auth/* endpoints.

import { adminAuthStorage } from "@auth/storage"
import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios"

import { env } from "@/env"

type RetryableRequest = InternalAxiosRequestConfig & { _retry?: boolean }

export const apiClient = axios.create({
  baseURL: `${env.VITE_API_BASE_URL}/api`,
  headers: { "Content-Type": "application/json" },
  timeout: 15_000,
})

// Attach the admin JWT (aud: "admin") on every request.
apiClient.interceptors.request.use((config) => {
  const token = adminAuthStorage.getAccess()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Endpoints whose 401 must reach the caller untouched — the login form renders
// its own error, and the refresh endpoint is the refresh itself (intercepting
// it would loop).
const AUTH_ENDPOINTS = ["/admin/auth/login", "/admin/auth/refresh"]

function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false
  return AUTH_ENDPOINTS.some((path) => url.endsWith(path))
}

function logoutAndRedirect() {
  adminAuthStorage.clear()
  // `?motivo=sesion` lets the login page say the session ended instead of
  // dropping the superuser on a blank form mid-task.
  if (typeof window !== "undefined") window.location.href = "/login?motivo=sesion"
}

// On 401, try to refresh once, then replay the original request.
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryableRequest | undefined
    if (!original) return Promise.reject(error)

    if (error.response?.status === 401 && !original._retry && !isAuthEndpoint(original.url)) {
      const refresh = adminAuthStorage.getRefresh()
      if (!refresh) {
        logoutAndRedirect()
        return Promise.reject(error)
      }
      original._retry = true
      try {
        const { data } = await axios.post<{ access: string; refresh: string }>(
          `${env.VITE_API_BASE_URL}/api/admin/auth/refresh`,
          { refresh },
        )
        // The refresh token rotates on every use (api/auth.py::rotate_refresh);
        // keeping the old one was a hard logout 7 days after sign-in.
        adminAuthStorage.setTokens(data.access, data.refresh)
        original.headers.Authorization = `Bearer ${data.access}`
        return apiClient(original)
      } catch {
        logoutAndRedirect()
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  },
)
